/**
 * CyFunReportPage - "Rapport CyFun" (remplace l'ancien Mapping CyFun, archive v1).
 *
 * Vue : référentiel CyFun officiel (219 exigences) filtré par le NIVEAU CIBLE de
 * l'organisation, avec pour chaque exigence : niveau, Key Measure, type
 * (Auto M365 / Auto Azure / Manuel / Externe), conformité (déduite des derniers
 * scans), présence de preuve, date du dernier scan.
 *
 * NB : niveau cible en localStorage pour l'instant (persistance backend +
 * snapshot dates + génération document = étape suivante).
 */
import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card, Badge, Body1, Caption1, Title3, Button, Dropdown, Option, Switch, Checkbox,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  tokens, MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled, ArrowDownload20Regular, Warning16Filled, DismissCircle16Filled,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { azureConnectionsApi } from '../api/azureConnectionsApi';
import { azureScansApi } from '../api/azureScansApi';
import {
  CYFUN_REQUIREMENTS, CYFUN_LEVELS, levelsForTarget,
  CYFUN_FUNCTION_LABELS, CYFUN_TYPE_LABELS,
  type CyFunLevel, type CyFunControlType, type CyFunRequirement,
} from '../domain/cyfunFramework';
import {
  downloadCyFunReportPdf, downloadCyFunReportDocx,
  downloadCyFunActionPlanPdf, downloadCyFunActionPlanDocx,
  type CyFunReportRow, type CyFunReportMeta,
} from '../reports/generateCyFunReport';
import { cyfunApi } from '../api/cyfunApi';
import { onboardingApi } from '../api/onboardingApi';
import { controlsById } from '../domain/controls';
import { AZURE_CONTROLS } from '../domain/azureControls';
import { confStats, buildControlInventory } from '../reports/cyfunReportData';

type Conf = 'pass' | 'fail' | 'warning' | 'na' | 'manual' | 'external' | 'partial';

// Index id -> titre pour la sous-ligne de mapping (contrôles couvrant l'exigence).
const AZURE_TITLE_BY_ID: Record<string, string> = Object.fromEntries(
  AZURE_CONTROLS.map((c) => [c.id, c.title]),
);

const LEVEL_COLOR: Record<CyFunLevel, { fg: string; bg: string }> = {
  Basic:     { fg: '#1F7A45', bg: '#E6F4EC' },
  Important: { fg: '#A86317', bg: '#FDF1E2' },
  Essential: { fg: '#B82329', bg: '#FBE7E7' },
};
const TYPE_COLOR: Record<CyFunControlType, { fg: string; bg: string }> = {
  m365:     { fg: '#1F3D5C', bg: '#E5EAF0' },
  azure:    { fg: '#0072C6', bg: '#E1F0FB' },
  manual:   { fg: '#4A4A6E', bg: '#E9E9F2' },
  external: { fg: '#7A7A7A', bg: '#EEEEEE' },
};

const TARGET_KEY = 'nis365.cyfun.targetLevel';

interface ScanHit { status: string; date?: string; hasEvidence: boolean; message?: string; }

type ReqRow = CyFunRequirement & { conf: Conf; hasEvidence: boolean; lastDate?: string };

// Mappe un statut de scan brut vers le type Conf affiché par ConfBadge.
function statusToConf(status?: string): Conf {
  const s = (status ?? '').toLowerCase();
  if (s === 'pass') return 'pass';
  if (s === 'fail' || s === 'error') return 'fail';
  if (s === 'warning' || s === 'warn') return 'warning';
  if (s === 'manual') return 'manual';
  if (s === 'external') return 'external';
  return 'na';
}

export function CyFunReportPage() {
  const { t, i18n } = useTranslation();
  const { tenant }  = useActiveTenant();
  const m365Scan    = useLatestScan(tenant.id);

  // Niveau cible — persisté côté client (backend), fallback Important.
  const configQ = useQuery({ queryKey: ['cyfun-config'], queryFn: () => cyfunApi.getConfig(), staleTime: 60_000, retry: false });
  // Identité entreprise pour la page de garde + chapitre du rapport.
  const orgQ = useQuery({ queryKey: ['onboarding-status'], queryFn: () => onboardingApi.status(), staleTime: 60_000, retry: false });
  const [target, setTargetState] = useState<CyFunLevel>('Important');
  useEffect(() => {
    const tl = configQ.data?.targetLevel as CyFunLevel | undefined;
    if (tl && CYFUN_LEVELS.includes(tl)) setTargetState(tl);
  }, [configQ.data]);
  const setTarget = (lvl: CyFunLevel) => { setTargetState(lvl); void cyfunApi.saveConfig({ targetLevel: lvl }); };

  // Filtres
  // Type : multi-sélection (cases à cocher). "Manuel" exclu par défaut.
  const [typeFilter, setTypeFilter] = useState<Set<CyFunControlType>>(
    () => new Set<CyFunControlType>(['m365', 'azure', 'external']),
  );
  const [confFilter, setConfFilter] = useState<'all' | 'conforme' | 'partiel' | 'nonconforme' | 'na'>('all');
  const [kmOnly, setKmOnly]         = useState(true);
  // Exigence sélectionnée -> ouvre la fenêtre détail (résultat par contrôle)
  const [selected, setSelected]     = useState<ReqRow | null>(null);

  // Dernier scan Azure (1ere connexion)
  const connQ = useQuery({ queryKey: ['azure-connections'], queryFn: () => azureConnectionsApi.list(), staleTime: 60_000, retry: false });
  const connId = connQ.data?.connections?.[0]?.id;
  const azureScanQ = useQuery({
    queryKey: ['azure-latest', connId],
    queryFn:  () => azureScansApi.latest(connId!),
    enabled:  !!connId, staleTime: 10_000, retry: false,
  });

  // Index controlId(lowercase) -> resultat de scan
  const scanByControl = useMemo(() => {
    const map = new Map<string, ScanHit>();
    // base persistante : snapshot stocké (dates qui survivent aux purges de scan)
    for (const [cid, v] of Object.entries(configQ.data?.scanSnapshot ?? {})) {
      map.set(cid.toLowerCase(), { status: v.status ?? '', date: v.date, hasEvidence: !!v.hasEvidence });
    }
    const m365Results = (m365Scan as unknown as { results?: Array<Record<string, unknown>> } | null)?.results ?? [];
    for (const r of m365Results) {
      const cid = String(r.controlId ?? r.ControlId ?? '').toLowerCase();
      if (!cid) continue;
      const evidences = (r.evidences ?? r.Evidences ?? []) as unknown[];
      map.set(cid, {
        status:      String(r.status ?? r.Status ?? '').toLowerCase(),
        date:        (r.evaluatedAt ?? r.EvaluatedAt) as string | undefined,
        hasEvidence: Array.isArray(evidences) && evidences.length > 0,
        message:     (String(r.message ?? r.Message ?? '') || undefined),
      });
    }
    for (const r of (azureScanQ.data?.results ?? [])) {
      const cid = r.controlId.toLowerCase();
      map.set(cid, { status: r.status, date: r.evaluatedAt, hasEvidence: !!r.evidenceUrl, message: r.message });
    }
    return map;
  }, [m365Scan, azureScanQ.data, configQ.data]);

  // Snapshot : dès qu'on a des résultats de scan live, on les persiste (upsert) par contrôle.
  useEffect(() => {
    const live: Record<string, { date?: string; status?: string; hasEvidence?: boolean }> = {};
    const m365Results = (m365Scan as unknown as { results?: Array<Record<string, unknown>> } | null)?.results ?? [];
    for (const r of m365Results) {
      const cid = String(r.controlId ?? r.ControlId ?? '').toLowerCase();
      if (!cid) continue;
      const ev = (r.evidences ?? r.Evidences ?? []) as unknown[];
      live[cid] = { status: String(r.status ?? r.Status ?? '').toLowerCase(), date: (r.evaluatedAt ?? r.EvaluatedAt) as string | undefined, hasEvidence: Array.isArray(ev) && ev.length > 0 };
    }
    for (const r of (azureScanQ.data?.results ?? [])) {
      live[r.controlId.toLowerCase()] = { status: r.status, date: r.evaluatedAt, hasEvidence: !!r.evidenceUrl };
    }
    if (Object.keys(live).length > 0) void cyfunApi.saveConfig({ scanSnapshot: live });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m365Scan, azureScanQ.data]);

  // Calcul conformité/preuve/date par exigence
  function evaluate(reqM365: string[], reqAzure: string[], type: CyFunControlType) {
    const ids = [...reqM365, ...reqAzure].map((x) => x.toLowerCase());
    const hits = ids.map((id) => scanByControl.get(id)).filter(Boolean) as ScanHit[];
    let conf: Conf;
    if (type === 'external') {
      conf = 'external';
    } else {
      // On ne compte que les contrôles réellement évalués (pass/fail/warning).
      const scored = hits.filter((h) => ['pass', 'fail', 'error', 'warning', 'warn'].includes(h.status));
      if (scored.length === 0) {
        conf = type === 'manual' ? 'manual' : 'na';
      } else {
        const pass = scored.filter((h) => h.status === 'pass').length;
        const fail = scored.filter((h) => h.status === 'fail' || h.status === 'error').length;
        if (pass === scored.length)      conf = 'pass';      // tout conforme
        else if (pass === 0)             conf = fail > 0 ? 'fail' : 'warning';
        else                             conf = 'partial';   // mix conforme / non conforme
      }
    }
    const hasEvidence = hits.some((h) => h.hasEvidence);
    const dates = hits.map((h) => h.date).filter(Boolean) as string[];
    const lastDate = dates.length ? dates.sort().slice(-1)[0] : undefined;
    return { conf, hasEvidence, lastDate };
  }

  const allowedLevels = levelsForTarget(target);

  const rows = useMemo(() => {
    return CYFUN_REQUIREMENTS
      .filter((r) => allowedLevels.includes(r.level))
      .map((r) => ({ ...r, ...evaluate(r.m365, r.azure, r.type) }))
      .filter((r) => r.id)
      .filter((r) => {
        if (!typeFilter.has(r.type)) return false;
        if (kmOnly && !r.keyMeasure) return false;
        if (confFilter === 'conforme'    && r.conf !== 'pass') return false;
        if (confFilter === 'partiel'     && !(r.conf === 'partial' || r.conf === 'warning')) return false;
        if (confFilter === 'nonconforme' && r.conf !== 'fail') return false;
        if (confFilter === 'na'          && !(r.conf === 'na' || r.conf === 'manual' || r.conf === 'external')) return false;
        return true;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedLevels.join(), Array.from(typeFilter).sort().join(), confFilter, kmOnly, scanByControl]);

  // Plan d'action rapide : key measures non conformes (ou non évaluées) au niveau cible
  const actionItems = useMemo(
    () => rows.filter((r) => r.keyMeasure && (r.conf === 'fail' || r.conf === 'warning' || r.conf === 'partial')),
    [rows],
  );

  const stats = confStats(rows);
  const { total, conformes, partiels, nonConf, nonEval, evaluated, score } = stats;

  function buildReportData(): { rr: CyFunReportRow[]; meta: CyFunReportMeta } {
    const mkResult = (id: string, kind: 'm365' | 'azure') => {
      const hit = scanByControl.get(id.toLowerCase());
      return {
        id, kind,
        title: kind === 'azure' ? (AZURE_TITLE_BY_ID[id] ?? '') : (controlsById[id]?.title ?? ''),
        status: hit?.status ?? '',
        message: hit?.message,
        date: hit?.date,
        hasEvidence: hit?.hasEvidence ?? false,
      };
    };
    const rr: CyFunReportRow[] = rows.map((r) => ({
      id: r.id, level: r.level, function: r.function, category: r.category,
      requirement: r.requirement, keyMeasure: r.keyMeasure, type: r.type,
      conf: r.conf, hasEvidence: r.hasEvidence, lastDate: r.lastDate,
      controls: [...r.m365, ...r.azure, ...r.manual],
      controlResults: [
        ...r.m365.map((id) => mkResult(id, 'm365')),
        ...r.azure.map((id) => mkResult(id, 'azure')),
      ],
    }));
    const o = orgQ.data;
    const meta: CyFunReportMeta = {
      orgName: tenant.name,
      targetLevel: target,
      date: new Date().toISOString(),
      referential: 'CCB CyberFundamentals (CyFun) v2024-01-08',
      logo: o?.companyLogo ?? undefined,
      company: {
        legalName:  o?.companyLegalName  ?? undefined,
        bce:        o?.companyBce        ?? undefined,
        address:    o?.companyAddress    ?? undefined,
        postalCode: o?.companyPostalCode ?? undefined,
        city:       o?.companyCity       ?? undefined,
        country:    o?.companyCountry    ?? undefined,
        contact:    o?.techContact       ?? undefined,
      },
    };
    return { rr, meta };
  }
  function onGenerateReport() {
    const { rr, meta } = buildReportData();
    const inventory = buildControlInventory(scanByControl);
    downloadCyFunReportPdf(rr, meta, inventory);
    void downloadCyFunReportDocx(rr, meta, inventory);
  }
  function onGenerateActionPlan() {
    const { rr, meta } = buildReportData();
    downloadCyFunActionPlanPdf(rr, meta);
    void downloadCyFunActionPlanDocx(rr, meta);
  }

  return (
    <div>
      <PageHeader
        title={t('cyfun.title')}
        description={t('cyfun.reportSubtitle', 'Référentiel CyFun officiel (CCB CyberFundamentals) appliqué à votre organisation.')}
      />

      {/* Niveau cible + synthèse */}
      <Card style={{ padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'row', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
            {t('cyfun.targetLevel', 'Niveau cible')}
          </Caption1>
          <div style={{ marginTop: 4 }}>
            <Dropdown
              value={target}
              selectedOptions={[target]}
              onOptionSelect={(_, d) => setTarget((d.optionValue as CyFunLevel) ?? 'Important')}
              style={{ minWidth: 160 }}
            >
              {CYFUN_LEVELS.map((l) => <Option key={l} value={l}>{l}</Option>)}
            </Dropdown>
          </div>
        </div>
        <Stat label={t('cyfun.stat.total', 'Exigences')} value={total} color={tokens.colorNeutralForeground1} />
        {/* v2026.06.165 - Partiel passe d'orange à vert clair (sémantique "en cours, partiellement OK") */}
        <Stat label={t('cyfun.stat.conforme', 'Conformes')} value={conformes} color="#1F7A45" />
        <Stat label={t('cyfun.stat.partiel', 'Partiels')} value={partiels} color="#6EA84A" />
        <Stat label={t('cyfun.stat.nonconforme', 'Non conformes')} value={nonConf} color="#B82329" />
        <Stat label={t('cyfun.stat.nonevalue', 'Non évalués')} value={nonEval} color="#7A7A7A" />
        <Stat label="Key measures" value={stats.keyMeasures} color="#A86317" />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button appearance="primary" icon={<ArrowDownload20Regular />} onClick={onGenerateReport}>
            Générer Rapport CyFun Ready
          </Button>
          <Button appearance="secondary" icon={<ArrowDownload20Regular />} onClick={onGenerateActionPlan}>
            Prévoir un plan d’action rapide
          </Button>
        </div>
        <ConformityGauge conformes={conformes} partiels={partiels} nonConf={nonConf} nonEval={nonEval} total={total} evaluated={evaluated} score={score} />
      </Card>

      {/* Filtres (inline). Le plan d'action key measures part dans le document généré. */}
      <Card style={{ padding: 12, marginBottom: 16, display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', fontSize: 11 }}>{t('cyfun.filter.type', 'Type')}</Caption1>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
            {([['m365', 'Auto M365'], ['azure', 'Auto Azure'], ['manual', 'Manuel'], ['external', 'Externe']] as [CyFunControlType, string][]).map(([k, lbl]) => (
              <Checkbox
                key={k}
                label={lbl}
                checked={typeFilter.has(k)}
                onChange={(_, d) => setTypeFilter((prev) => {
                  const next = new Set(prev);
                  if (d.checked) next.add(k); else next.delete(k);
                  return next;
                })}
              />
            ))}
          </div>
        </div>
        <FilterDrop label={t('cyfun.filter.conf', 'Conformité')} value={confFilter}
          options={[['all', 'Toutes'], ['conforme', 'Conforme'], ['partiel', 'Partiel'], ['nonconforme', 'Non conforme'], ['na', 'Non évalué / Manuel / Externe']]}
          onChange={(v) => setConfFilter(v as 'all' | 'conforme' | 'partiel' | 'nonconforme' | 'na')} />
        <Switch checked={kmOnly} onChange={(_, d) => setKmOnly(d.checked)} label="Key measures uniquement" />
      </Card>

      {/* Tableau */}
      <Card>
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: 110 }}>Code</TableHeaderCell>
              <TableHeaderCell>Exigence</TableHeaderCell>
              <TableHeaderCell style={{ width: 90 }}>Niveau</TableHeaderCell>
              <TableHeaderCell style={{ width: 90 }}>Key mesure</TableHeaderCell>
              <TableHeaderCell style={{ width: 110 }}>Type</TableHeaderCell>
              <TableHeaderCell style={{ width: 120 }}>Conformité</TableHeaderCell>
              <TableHeaderCell style={{ width: 60 }}>Preuve</TableHeaderCell>
              <TableHeaderCell style={{ width: 110 }}>Dernier scan</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                <TableCell><code style={{ fontFamily: 'Consolas, monospace', fontWeight: 600, color: tokens.colorBrandForeground1 }}>{r.id}</code></TableCell>
                <TableCell>
                  <Body1>{r.requirement}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                    {CYFUN_FUNCTION_LABELS[r.function] ?? r.function} · {r.category}
                  </Caption1>
                  <CtrlMapping m365={r.m365} azure={r.azure} />
                </TableCell>
                <TableCell><Badge appearance="filled" style={{ background: LEVEL_COLOR[r.level].bg, color: LEVEL_COLOR[r.level].fg, fontWeight: 700 }}>{r.level}</Badge></TableCell>
                <TableCell>{r.keyMeasure ? <CheckmarkCircle16Filled style={{ color: '#A86317' }} /> : <span style={{ color: tokens.colorNeutralForeground4 }}>—</span>}</TableCell>
                <TableCell><Badge appearance="filled" style={{ background: TYPE_COLOR[r.type].bg, color: TYPE_COLOR[r.type].fg, fontWeight: 600 }}>{CYFUN_TYPE_LABELS[r.type]}</Badge></TableCell>
                <TableCell><ConfBadge conf={r.conf} /></TableCell>
                <TableCell>{r.hasEvidence ? <CheckmarkCircle16Filled style={{ color: '#1F7A45' }} /> : <span style={{ color: tokens.colorNeutralForeground4 }}>—</span>}</TableCell>
                <TableCell><Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{r.lastDate ? new Date(r.lastDate).toLocaleDateString(i18n.language) : '—'}</Caption1></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && (
          <MessageBar intent="info" style={{ margin: 12 }}><MessageBarBody>Aucune exigence pour ces filtres.</MessageBarBody></MessageBar>
        )}
      </Card>

      {/* Fenêtre détail : résultat de chaque test (contrôle) rattaché à l'exigence */}
      {selected && (
        <Dialog open onOpenChange={(_, d) => { if (!d.open) setSelected(null); }}>
          <DialogSurface style={{ maxWidth: 780 }}>
            <DialogBody>
              <DialogTitle>
                <code style={{ fontFamily: 'Consolas, monospace', color: tokens.colorBrandForeground1 }}>{selected.id}</code>
                {'  '}— résultats des tests
              </DialogTitle>
              <DialogContent>
                <Body1 block style={{ marginBottom: 4 }}>{selected.requirement}</Body1>
                <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: 14 }}>
                  {CYFUN_FUNCTION_LABELS[selected.function] ?? selected.function} · {selected.category} · Niveau {selected.level}
                </Caption1>

                {[...selected.m365, ...selected.azure].length === 0 ? (
                  <MessageBar intent="info"><MessageBarBody>Aucun contrôle automatisé rattaché à cette exigence (manuelle ou externe).</MessageBarBody></MessageBar>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      ...selected.m365.map((id) => ({ id, kind: 'm365' as const, title: controlsById[id]?.title ?? '' })),
                      ...selected.azure.map((id) => ({ id, kind: 'azure' as const, title: AZURE_TITLE_BY_ID[id] ?? '' })),
                    ].map((c) => {
                      const hit = scanByControl.get(c.id.toLowerCase());
                      return (
                        <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 6 }}>
                          <code style={{ fontFamily: 'Consolas, monospace', fontWeight: 600, color: c.kind === 'azure' ? '#0072C6' : '#1F3D5C', minWidth: 88, flexShrink: 0 }}>{c.id}</code>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Body1 block style={{ fontSize: 13 }}>{c.title}</Body1>
                            {hit?.message && <Caption1 style={{ color: tokens.colorNeutralForeground2, display: 'block', marginTop: 2 }}>{hit.message}</Caption1>}
                            {hit?.date && <Caption1 style={{ color: tokens.colorNeutralForeground4, display: 'block', marginTop: 2 }}>Évalué le {new Date(hit.date).toLocaleDateString(i18n.language)}</Caption1>}
                            {!hit && <Caption1 style={{ color: tokens.colorNeutralForeground4, display: 'block', marginTop: 2 }}>Pas encore évalué (aucun scan).</Caption1>}
                          </div>
                          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                            <ConfBadge conf={statusToConf(hit?.status)} />
                            {hit?.hasEvidence && <Caption1 style={{ color: '#1F7A45' }}>preuve ✓</Caption1>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setSelected(null)}>Fermer</Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <Caption1 style={{ color: tokens.colorNeutralForeground3, fontSize: 11 }}>{label}</Caption1>
    </div>
  );
}

// Jauge de taux de conformité pondéré (Conforme = 1, Partiel = 0,5).
// L'échelle couvre TOUTES les exigences du niveau (y compris Non évalué),
// mais le score % reste pondéré sur le seul périmètre évalué.
function ConformityGauge({ conformes, partiels, nonConf, nonEval, total, evaluated, score }: {
  conformes: number; partiels: number; nonConf: number; nonEval: number; total: number; evaluated: number; score: number;
}) {
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  const scoreColor = score >= 80 ? '#1F7A45' : score >= 50 ? '#A86317' : '#B82329';
  // v2026.06.165 - Partiel = vert clair ; Non évalué ajouté à l'échelle (gris).
  const segments: [string, number, string][] = [
    ['Conforme', conformes, '#1F7A45'],
    ['Partiel', partiels, '#6EA84A'],
    ['Non conforme', nonConf, '#B82329'],
    ['Non évalué', nonEval, '#B9C2CC'],
  ];
  return (
    <div style={{ flexBasis: '100%', marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
          Taux de conformité (pondéré)
        </Caption1>
        <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score}%</span>
      </div>
      <div style={{ height: 12, borderRadius: 6, background: '#EEEEEE', overflow: 'hidden', display: 'flex' }}>
        {segments.map(([label, n, color]) => (
          <div key={label} style={{ width: `${pct(n)}%`, background: color }} title={`${label} : ${n}`} />
        ))}
      </div>
      {/* Légende de l'échelle */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
        {segments.map(([label, n, color]) => (
          <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: tokens.colorNeutralForeground3 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />
            {label} ({n})
          </span>
        ))}
      </div>
      <Caption1 style={{ color: tokens.colorNeutralForeground4, display: 'block', marginTop: 4 }}>
        Score pondéré : Conforme = 1 · Partiel = 0,5 · Non conforme = 0 — sur {evaluated} exigence{evaluated > 1 ? 's' : ''} évaluée{evaluated > 1 ? 's' : ''} (hors Manuel / Externe / Non évalué).
      </Caption1>
    </div>
  );
}

function FilterDrop({ label, value, options, onChange }: {
  label: string; value: string; options: [string, string][]; onChange: (v: string) => void;
}) {
  const current = options.find(([k]) => k === value)?.[1] ?? value;
  return (
    <div>
      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', fontSize: 11 }}>{label}</Caption1>
      <Dropdown size="small" value={current} selectedOptions={[value]} onOptionSelect={(_, d) => onChange(d.optionValue ?? 'all')} style={{ minWidth: 150 }}>
        {options.map(([k, lbl]) => <Option key={k} value={k}>{lbl}</Option>)}
      </Dropdown>
    </div>
  );
}

// Sous-ligne : contrôles M365 + Azure couvrant l'exigence (id — titre).
function CtrlMapping({ m365, azure }: { m365: string[]; azure: string[] }) {
  const items: { id: string; title: string; kind: 'm365' | 'azure' }[] = [
    ...m365.map((id) => ({ id, title: controlsById[id]?.title ?? '', kind: 'm365' as const })),
    ...azure.map((id) => ({ id, title: AZURE_TITLE_BY_ID[id] ?? '', kind: 'azure' as const })),
  ].filter((x) => x.id);
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: `2px solid ${tokens.colorNeutralStroke2}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((it) => (
        <div key={it.id} style={{ fontSize: 11.5, color: tokens.colorNeutralForeground2, display: 'flex', gap: 6, alignItems: 'baseline' }}>
          <code style={{ fontFamily: 'Consolas, monospace', fontWeight: 600, color: it.kind === 'azure' ? '#0072C6' : '#1F3D5C', minWidth: 82, flexShrink: 0 }}>{it.id}</code>
          <span>— {it.title}</span>
        </div>
      ))}
    </div>
  );
}

function ConfBadge({ conf }: { conf: Conf }) {
  const cfg: Record<Conf, { label: string; fg: string; bg: string; icon?: JSX.Element }> = {
    pass:     { label: 'Conforme',     fg: '#1F7A45', bg: '#DCF0E5', icon: <CheckmarkCircle16Filled /> },
    fail:     { label: 'Non conforme', fg: '#B82329', bg: '#FBE7E7', icon: <DismissCircle16Filled /> },
    warning:  { label: 'À améliorer',  fg: '#A86317', bg: '#FBEFDD', icon: <Warning16Filled /> },
    // v2026.06.165 - Partiel en vert clair (sémantique "en cours, partiellement OK")
    partial:  { label: 'Partiel',      fg: '#3F7A1E', bg: '#E9F4DF' },
    na:       { label: 'Non évalué',   fg: '#7A7A7A', bg: '#EEEEEE' },
    manual:   { label: 'Manuel',       fg: '#4A4A6E', bg: '#E9E9F2' },
    external: { label: 'Externe',      fg: '#7A7A7A', bg: '#EEEEEE' },
  };
  const c = cfg[conf];
  return <Badge appearance="filled" style={{ background: c.bg, color: c.fg, fontWeight: 600 }}>{c.label}</Badge>;
}
